import { useState } from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import ListAltIcon from "@mui/icons-material/ListAlt";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import Button from "src/components/Button";
import { PageHeader, StyledPaper } from "src/components/Base";
import Hint from "src/components/Hint";
import Spinner from "src/components/Spinner";
import { SettingKey } from "src/shared/api-types/user";
import type {
  CreateLimitationDefinitionBody,
  LimitationDefinition,
  UpdateLimitationDefinitionBody,
} from "src/shared/api-types/limitations";

import { useLimitations } from "./hooks/useLimitations";
import { createFormValuesFromTemplate, ensureGeneratedCodes } from "./limitationFormValues";
import { getLimitationPreviewValues } from "./limitationDisplay";
import { limitationTemplates } from "./limitationTemplates";
import LimitationDefinitionDialog from "./components/LimitationDefinitionDialog";
import LimitationUsageDialog from "./components/LimitationUsageDialog";
import LimitationsTable from "./components/LimitationsTable";

interface LimitationsManagementProps {
  crewId: number;
}

const SHOW_LIMITATION_SETS_UI = false;

const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

const LimitationsManagement = ({ crewId }: LimitationsManagementProps) => {
  const { t } = useTranslation();
  const {
    limitations,
    limitationSetsEnabled,
    isLoading,
    reload,
    updateSetsEnabled,
    create,
    update,
    archive,
    unarchive,
    reorder,
  } = useLimitations(crewId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preparedOpen, setPreparedOpen] = useState(false);
  const [selectedPrepared, setSelectedPrepared] = useState<string[]>([]);
  const [selectedDefinition, setSelectedDefinition] = useState<LimitationDefinition | null>(null);
  const [usageDefinition, setUsageDefinition] = useState<LimitationDefinition | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [activeTab, setActiveTab] = useState<"limitations" | "sets">("limitations");
  const [isArchiveActionLoading, setIsArchiveActionLoading] = useState(false);
  const visibleLimitations = showArchived ? limitations : limitations.filter((limitation) => !limitation.archived);
  const hasSelectedPrepared = selectedPrepared.length > 0;
  const setsTabVisible = SHOW_LIMITATION_SETS_UI && limitationSetsEnabled;

  const existingTemplateCodes = new Set(limitations.map((limitation) => limitation.code));
  const preparedTemplates = limitationTemplates.filter(
    (template) => template.key !== "custom" && !existingTemplateCodes.has(template.code),
  );

  const handleAddCustom = () => {
    blurActiveElement();
    setSelectedDefinition(null);
    setDialogOpen(true);
  };

  const handleTogglePrepared = (key: string) => {
    setSelectedPrepared((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  const handleToggleAllPrepared = () => {
    setSelectedPrepared((prev) =>
      prev.length === preparedTemplates.length ? [] : preparedTemplates.map((template) => template.key),
    );
  };

  const handleSetsEnabledChange = async (enabled: boolean) => {
    await updateSetsEnabled(enabled);
    if (!enabled) setActiveTab("limitations");
  };

  const handleAddPrepared = async () => {
    if (selectedPrepared.length === 0) return;
    for (let index = 0; index < selectedPrepared.length; index += 1) {
      const key = selectedPrepared[index];
      const template = preparedTemplates.find((item) => item.key === key);
      if (!template) continue;
      const values = createFormValuesFromTemplate(
        template,
        t(`limitations.templates.${template.key}`),
        limitations.length + index,
      );
      const generatedValues = ensureGeneratedCodes(values);
      await create(
        {
          ...generatedValues,
          unit: generatedValues.unit || null,
        },
        { reload: false },
      );
    }
    await reload();
    setSelectedPrepared([]);
    setPreparedOpen(false);
  };

  const handleEdit = (definition: LimitationDefinition) => {
    blurActiveElement();
    setSelectedDefinition(definition);
    setDialogOpen(true);
  };

  const handleSubmit = async (body: CreateLimitationDefinitionBody | UpdateLimitationDefinitionBody) => {
    if (selectedDefinition) {
      await update(selectedDefinition.id, body as UpdateLimitationDefinitionBody);
      return;
    }
    await create(body as CreateLimitationDefinitionBody);
  };

  const handleMove = async (definition: LimitationDefinition, direction: -1 | 1) => {
    const orderedScope = showArchived ? limitations : visibleLimitations;
    const index = orderedScope.findIndex((limitation) => limitation.id === definition.id);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= orderedScope.length) return;
    const reorderedScope = [...orderedScope];
    [reorderedScope[index], reorderedScope[targetIndex]] = [reorderedScope[targetIndex], reorderedScope[index]];
    let visibleIndex = 0;
    const next = showArchived
      ? reorderedScope
      : limitations.map((limitation) => {
          if (limitation.archived) return limitation;
          const reorderedLimitation = reorderedScope[visibleIndex];
          visibleIndex += 1;
          return reorderedLimitation;
        });
    await reorder(next);
  };

  const handleArchive = async (definition: LimitationDefinition) => {
    setIsArchiveActionLoading(true);
    try {
      await archive(definition.id);
    } finally {
      setIsArchiveActionLoading(false);
    }
  };

  const handleUnarchive = async (definition: LimitationDefinition) => {
    setIsArchiveActionLoading(true);
    try {
      await unarchive(definition.id);
    } finally {
      setIsArchiveActionLoading(false);
    }
  };

  return (
    <>
      <PageHeader>{t("limitations.title")}</PageHeader>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={setsTabVisible ? activeTab : "limitations"}
          onChange={(_, value: "limitations" | "sets") => setActiveTab(value)}
          aria-label={String(t("limitations.navigation"))}
        >
          <Tab
            value="limitations"
            icon={<ListAltIcon />}
            iconPosition="start"
            label={t("limitations.tabs.limitations")}
          />
          {setsTabVisible && (
            <Tab
              value="sets"
              icon={<DashboardCustomizeIcon />}
              iconPosition="start"
              label={t("limitations.tabs.sets")}
            />
          )}
        </Tabs>
        {SHOW_LIMITATION_SETS_UI && (
          <FormControlLabel
            sx={{ mx: { xs: 0, sm: 1 }, alignSelf: { xs: "flex-end", sm: "center" } }}
            control={
              <Switch
                checked={limitationSetsEnabled}
                onChange={(event) => handleSetsEnabledChange(event.target.checked)}
              />
            }
            label={t("limitations.use_sets")}
          />
        )}
      </Box>
      <Hint settingKey={SettingKey.LIMITATION_DEFINITION_STRUCTURE_HINT} sx={{ mb: 2 }}>
        {t("limitations.company_list_description")} {t("limitations.structure_hint")}
      </Hint>
      {setsTabVisible && (
        <Hint settingKey={SettingKey.LIMITATION_SETS_HINT} sx={{ mb: 2 }}>
          {t("limitations.sets_hint")}
        </Hint>
      )}
      {setsTabVisible && activeTab === "sets" ? (
        <StyledPaper variant="outlined">
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("limitations.tabs.sets")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {limitationSetsEnabled ? t("limitations.sets_placeholder") : t("limitations.sets_disabled")}
          </Typography>
        </StyledPaper>
      ) : (
        <StyledPaper variant="outlined">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="h6">{t("limitations.company_list_title")}</Typography>
              <Tooltip title={showArchived ? t("limitations.hide_archived") : t("limitations.show_archived")}>
                <IconButton onClick={() => setShowArchived((prev) => !prev)} size="medium">
                  {showArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: { sm: "flex-end" },
                gap: 1,
                "& .MuiButton-root": {
                  width: { xs: "100%", sm: "max-content" },
                  mr: "0 !important",
                },
              }}
            >
              {preparedTemplates.length > 0 && (
                <Button
                  onClick={
                    preparedOpen && hasSelectedPrepared ? handleAddPrepared : () => setPreparedOpen((prev) => !prev)
                  }
                  startIcon={<AddIcon />}
                  color="secondary"
                  variant={preparedOpen ? "contained" : "outlined"}
                >
                  {preparedOpen && hasSelectedPrepared
                    ? t("limitations.add_selected", { count: selectedPrepared.length })
                    : t("limitations.add_predefined")}
                </Button>
              )}
              <Button onClick={handleAddCustom} startIcon={<AddIcon />}>
                {t("limitations.add_custom")}
              </Button>
            </Box>
          </Box>
          {preparedOpen && (
            <Box sx={{ mb: 3, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPrepared.length === preparedTemplates.length}
                      indeterminate={selectedPrepared.length > 0 && selectedPrepared.length < preparedTemplates.length}
                      onChange={handleToggleAllPrepared}
                    />
                  }
                  label={t("limitations.select_all")}
                />
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>{t("limitations.name")}</TableCell>
                      <TableCell>{t("limitations.type")}</TableCell>
                      <TableCell>{t("limitations.details")}</TableCell>
                      <TableCell>{t("limitations.values")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preparedTemplates.map((template) => (
                      <TableRow
                        key={template.key}
                        selected={selectedPrepared.includes(template.key)}
                        onClick={() => handleTogglePrepared(template.key)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedPrepared.includes(template.key)}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => handleTogglePrepared(template.key)}
                          />
                        </TableCell>
                        <TableCell>{t(`limitations.templates.${template.key}`)}</TableCell>
                        <TableCell>{t(`limitations.types.${template.type}`)}</TableCell>
                        <TableCell>
                          {template.type === "number" ? `${template.operator} ${template.unit}` : ""}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {getLimitationPreviewValues(template.type, template.values, t).map((value, index) => (
                              <Chip key={`${template.key}-${index}`} label={value} size="small" />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {isLoading || isArchiveActionLoading ? (
            <Spinner message={t("limitations.loading")} />
          ) : (
            <LimitationsTable
              limitations={visibleLimitations}
              onEdit={handleEdit}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onMove={handleMove}
              onShowUsage={setUsageDefinition}
            />
          )}
        </StyledPaper>
      )}
      <LimitationDefinitionDialog
        open={dialogOpen}
        definition={selectedDefinition}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
      />
      <LimitationUsageDialog crewId={crewId} definition={usageDefinition} onClose={() => setUsageDefinition(null)} />
    </>
  );
};

export default LimitationsManagement;
